import React from 'react';
import StyleConstants from '../../../core/styles.constants';
import type { IconLabelFeature, TextLabelFeature } from '../models/editor-feature';
import { getMdiIconPath, MDI_VIEWBOX_SIZE } from '../utils/mdi-icons';
import { toScreen, type Projection } from '../utils/projection';

/** Selected reads strongest, hovered sits just under it, otherwise no outline at all. */
const outlineWidthFor = (
  selected: boolean,
  hovered: boolean,
  whenSelected: number,
  whenHovered: number,
): number => {
  if (selected) return whenSelected;
  if (hovered) return whenHovered;
  return 0;
};

interface Props {
  readonly label: TextLabelFeature | IconLabelFeature;
  readonly projection: Projection;
  readonly selected?: boolean;
  readonly hovered?: boolean;
  /**
   * Whether the MDI set has finished loading.
   *
   * Passed in rather than read from the module, and it matters twice. This component is
   * memoised, so without a prop that changes when the icons arrive React has no reason to
   * re-render it and every icon stays a placeholder until something else moves — which is why
   * the icons only appeared after a page reload, when the module was already cached before
   * the first render. It also stops the placeholder claiming an icon name is unknown while
   * the set it would be looked up in is still on its way.
   */
  readonly iconsLoaded?: boolean;
}

/**
 * A text or icon annotation.
 *
 * Both kinds rotate purely visually — the anchor point never moves — and both centre on
 * that point, matching how the mobile app draws them so the editor is a faithful preview.
 */
const AnnotationLabel = ({
  label,
  projection,
  selected = false,
  hovered = false,
  iconsLoaded = false,
}: Props): React.JSX.Element => {
  const { x, y } = toScreen(label.coordinates, projection);
  const transform = `translate(${x}, ${y}) rotate(${label.rotation})`;

  if (label.kind === 'textLabel') {
    return (
      <g transform={transform}>
        <text
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={label.fontSize}
          fontFamily={StyleConstants.HEADER_FONT_FAMILY}
          fill={label.color}
          stroke={selected || hovered ? StyleConstants.MEDIUM_BLUE_COLOR : 'none'}
          strokeWidth={outlineWidthFor(selected, hovered, 0.5, 0.3)}
        >
          {label.text}
        </text>
      </g>
    );
  }

  const path = getMdiIconPath(label.iconName);

  // Nothing at all until the icon set has arrived. The placeholder below means "this name is
  // not a real MDI icon", and that is not something that can be said before the set it would
  // be looked up in has loaded.
  if (!path && !iconsLoaded) {
    return <g transform={transform} />;
  }

  // An icon name that is not in this MDI major renders as nothing on the mobile app, with
  // no error anywhere — so the editor draws a visible placeholder instead. This is the only
  // place the mistake is ever visible to the person who made it.
  if (!path) {
    return (
      <g transform={transform}>
        <rect
          x={-label.iconSize / 2}
          y={-label.iconSize / 2}
          width={label.iconSize}
          height={label.iconSize}
          fill="none"
          stroke={StyleConstants.PINK_COLOR}
          strokeWidth={1}
          strokeDasharray="3 2"
          rx={2}
        />
        <text
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={label.iconSize * 0.6}
          fill={StyleConstants.PINK_COLOR}
        >
          ?
        </text>
      </g>
    );
  }

  // Scaled from MDI's own 24×24 canvas to the size stored on the annotation, and shifted so
  // the glyph centres on the anchor rather than hanging off its corner.
  const scale = label.iconSize / MDI_VIEWBOX_SIZE;

  return (
    <g transform={`${transform} scale(${scale}) translate(${-MDI_VIEWBOX_SIZE / 2}, ${-MDI_VIEWBOX_SIZE / 2})`}>
      <path d={path} fill={label.color} stroke={selected ? StyleConstants.MEDIUM_BLUE_COLOR : 'none'} strokeWidth={selected ? 1 : 0} />
    </g>
  );
};

export default React.memo(AnnotationLabel);
